import { faInputText } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { Fragment, Node as ProseMirrorNode, ResolvedPos, Slice } from "prosemirror-model";
import { EditorState, Plugin, Selection, TextSelection, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";

import { toggleMark } from "~/lib/prose/commands";

import { MarkExtension } from "../extension";

export default class PlaceholderMark extends MarkExtension {
	get name() {
		return "placeholder_mark";
	}

	get mark() {
		const className = cx("prose-placeholder-mark", { readonly: !this.options.editable });
		return {
			parseDOM: [
				{
					tag: "span",
					getAttrs: (node: HTMLElement) => node.classList.contains("prose-placeholder-mark"),
				},
			],
			toDOM() {
				return [
					"span",
					{
						class: className,
						spellcheck: false,
					},
				];
			},
		};
	}

	get plugins() {
		if (this.options.editable) return [];
		return [
			new Plugin({
				appendTransaction: (trs, oldState, newState) => {
					const wasMultiSelected = trs.some(tr => tr.getMeta("multiselect-adjusted"));
					if (wasMultiSelected) return;

					const wasMouseClick = trs.some(tr => tr.getMeta("pointer"));
					let shouldRewriteSelection = false;
					let selFromIntoPlaceholder = false;
					let selToIntoPlaceholder = false;
					const {
						selection: { $from, $to, from, to, empty },
					} = newState;

					if (empty) {
						shouldRewriteSelection = this.isPosInMiddleOfPlaceholder($from);
					} else {
						// $from was not inside placeholder before but now is
						if (!this.isPosInMiddleOfPlaceholder(oldState.selection.$from) && this.isPosInMiddleOfPlaceholder($from)) {
							selFromIntoPlaceholder = true;
						}

						// $to was not inside placeholder before but now is
						if (!this.isPosInMiddleOfPlaceholder(oldState.selection.$to) && this.isPosInMiddleOfPlaceholder($to)) {
							selToIntoPlaceholder = true;
						}
						shouldRewriteSelection = selFromIntoPlaceholder || selToIntoPlaceholder;
					}

					if (shouldRewriteSelection) {
						let newSelectionAnchor = this.findPlaceholderStartBefore(newState, from);
						let newSelectionHead = newSelectionAnchor;

						if (empty) {
							// This is to allow selection from left to right using keyboard.
							if (!wasMouseClick && oldState.selection.empty && oldState.selection.from === from - 1) {
								// when selection cross a placeholder from left to right, set it to the end of the placeholder
								newSelectionAnchor = this.findPlaceholderEndAfter(newState, from);
								newSelectionHead = newSelectionAnchor;
							}
						} else if (selFromIntoPlaceholder) {
							newSelectionAnchor = to;
							if (oldState.selection.from === from - 1) {
								newSelectionHead = this.findPlaceholderEndAfter(newState, from);
							}
						} else if (selToIntoPlaceholder) {
							newSelectionAnchor = from;
							if (oldState.selection.to === to + 1) {
								// normally when $to goes inside a placeholder, we want the whole placeholder to be selected
								// one exception is when $to try to cross a placeholder from right to left, we want to deselect it
								newSelectionHead = this.findPlaceholderStartBefore(newState, to);
							} else {
								newSelectionHead = this.findPlaceholderEndAfter(newState, to);
							}
						}

						const newTr = newState.tr;
						const anchor = newTr.doc.resolve(newSelectionAnchor);
						const head = newTr.doc.resolve(newSelectionHead);
						const newSelection = new TextSelection(anchor, head);
						newTr.setSelection(newSelection);
						return newTr;
					}
					return undefined;
				},
				props: {
					handleTextInput: (view: EditorView, _from: number, _to: number, _text: string) => {
						const {
							state: { selection, tr },
						} = view;
						const { $from, $to } = selection;
						const newTextNode = view.state.schema.text(_text);
						const { nodeBefore } = $from;
						const { nodeAfter } = $to;
						const placeholderBeforeSel = nodeBefore && PlaceholderMark.isEnabled(nodeBefore);
						const placeholderAfterSel = nodeAfter && PlaceholderMark.isEnabled(nodeAfter);
						if (!selection.empty) {
							if (!placeholderBeforeSel && !placeholderAfterSel) {
								// Check if start of selection is right at the start of a node with placeholder mark.
								// Though default input behaviour will replace the whole placeholder for this case,
								// it will keep the placeholder mark for the new text node, which is not expected.
								// Therefore handle this specific case manually.
								const { nodeAfter: nodeAfterFrom } = $from;

								// when the placeholder is right after the start of the selection
								const placeholderAfter = nodeAfterFrom && PlaceholderMark.isEnabled(nodeAfterFrom);

								if (placeholderAfter) {
									tr.replaceRangeWith($from.pos, $to.pos, newTextNode);
									const newSelection = new TextSelection(tr.doc.resolve($from.pos + newTextNode.nodeSize));
									tr.setSelection(newSelection);
									view.dispatch(tr);
									return true;
								}
								return false;
							}

							// there are placeholder mark and other content between the selection in this case
							const replacementStartsAt = placeholderBeforeSel
								? this.findPlaceholderStartBefore(view.state, $from.pos)
								: $from.pos;
							const replacementEndsAt = placeholderAfterSel
								? this.findPlaceholderEndAfter(view.state, $to.pos)
								: $to.pos;
							tr.replaceRangeWith(replacementStartsAt, replacementEndsAt, newTextNode);
							view.dispatch(tr);
							return true;
						}

						// empty selection in the middle of placeholder (which should not happen, just in case)
						if (this.isPosInMiddleOfPlaceholder($from)) {
							// nodeBefore and nodeAfter existence checked in isPosInMiddleOfPlaceholder
							if (!nodeBefore || !nodeAfter) return false;
							const startPos = $from.pos - nodeBefore.nodeSize;
							const endPos = this.findPlaceholderEndAfter(view.state, $from.pos);
							tr.replaceRangeWith(startPos, endPos, newTextNode);
							const newSelection = new TextSelection(tr.doc.resolve(startPos + newTextNode.nodeSize));
							tr.setSelection(newSelection);
							view.dispatch(tr);
							return true;
						}

						// empty selection right at the start of placeholder
						if (!placeholderBeforeSel && placeholderAfterSel) {
							const placeholderEndsAt = this.findPlaceholderEndAfter(view.state, $from.pos);
							tr.replaceRangeWith($from.pos, placeholderEndsAt, newTextNode);
							const newSelection = new TextSelection(tr.doc.resolve($from.pos + newTextNode.nodeSize));
							tr.setSelection(newSelection);
							view.dispatch(tr);
							return true;
						}

						// empty selection right at the end of placeholder
						if (placeholderBeforeSel && !placeholderAfterSel) {
							tr.replaceRangeWith($from.pos, $from.pos, newTextNode);
							const newSelection = new TextSelection(tr.doc.resolve($from.pos + newTextNode.nodeSize));
							tr.setSelection(newSelection);
							view.dispatch(tr);
							return true;
						}
						return false;
					},
					handleDoubleClick: (view: EditorView, pos: number) => {
						const {
							state: { doc },
						} = view;
						const resPos = doc.resolve(pos);
						// ignore double click on any position within a placeholder
						if (this.isPosInMiddleOfPlaceholder(resPos)) {
							return true;
						}
						return false;
					},
					transformPasted: slice => {
						// There could be cases that we don't want the content that's going to be pasted contain placeholder mark,
						// so before the content is pasted, we remove the placeholder mark for specific nodes if needed

						// In this case, when trying to paste a link to fill the placeholder
						// the 'slice' that's going to be paste will has at least both 'link' and 'placeholder' mark on it,
						// and since we don't allow these two marks coexist, we need to remove the second one.
						const removePredicate = (n: ProseMirrorNode) =>
							!!n.marks.length && n.marks.some(m => m.type.name === "link");
						const fragment = this.removeMark(removePredicate)(slice.content);
						return new Slice(fragment, slice.openStart, slice.openEnd);
					},
					handlePaste: (view, event, slice) => {
						if (!(event instanceof ClipboardEvent) || !event.clipboardData) return false;
						// if is pasting files we should let the filePaste plugin handle it
						if (event.clipboardData.files && event.clipboardData.files.length) return false;

						const {
							state: {
								selection: { $from, from, empty },
								tr,
							},
						} = view;
						if (empty && this.isPosAtStartOfPlaceholder($from)) {
							const placeholderEndsAt = this.findPlaceholderEndAfter(view.state, from);
							// replace the placeholder with the slice to be pasted
							tr.replace(from, placeholderEndsAt, slice);
							// set the selection to the end of the pasted slice
							const newSelection = new TextSelection(tr.doc.resolve(from + slice.size));
							tr.setSelection(newSelection);
							view.dispatch(tr);
							return true;
						}
						return false;
					},
					decorations: (state: EditorState) => {
						const {
							selection: { from, $from, empty },
							doc,
						} = state;
						// style the placeholder when the cursor is at the beginning of it
						if (empty && this.isPosAtStartOfPlaceholder($from)) {
							const decos: Decoration[] = [];
							const placeholderEndsAt = this.findPlaceholderEndAfter(state, from);
							decos.push(
								Decoration.inline(from, placeholderEndsAt, {
									class: cx("prose-placeholder-mark", "readonly", "active"),
								}),
							);
							return DecorationSet.create(doc, decos);
						}
						return undefined;
					},
				},
			}),
		];
	}

	keys() {
		if (this.options.editable) return null;
		return {
			Backspace: (state: EditorState, dispatch?: ((tr: Transaction) => void) | undefined): boolean => {
				if (!dispatch) return false;
				const { selection } = state;
				const { $from } = selection;
				const { nodeBefore } = $from;
				if (!nodeBefore) return false;
				if (!PlaceholderMark.isEnabled(nodeBefore)) return false;
				const placeholderStart = this.findPlaceholderStartBefore(state, $from.pos);
				const { tr } = state;
				const deletetionEndsAt = selection.empty ? $from.pos : selection.to;
				tr.deleteRange(placeholderStart, deletetionEndsAt);
				dispatch(tr);
				return true;
			},
			Delete: (state: EditorState, dispatch?: ((tr: Transaction) => void) | undefined): boolean => {
				if (!dispatch) return false;
				const { selection } = state;
				const { $to } = selection;
				const { nodeAfter } = $to;
				if (!nodeAfter) return false;
				if (!PlaceholderMark.isEnabled(nodeAfter)) return false;
				const placeholderEnd = this.findPlaceholderEndAfter(state, $to.pos);
				const { tr } = state;
				const deletetionStartsAt = selection.empty ? $to.pos : selection.from;
				tr.deleteRange(deletetionStartsAt, placeholderEnd);
				dispatch(tr);
				return true;
			},
		};
	}

	menuEntry() {
		if (!this.options.editable) return null;

		return {
			icon: faInputText,
			title: "Placeholder",
			run: () => toggleMark(this.$type, {}),
			section: "mostUsedAndSocial" as const,
			priority: 3,
		};
	}

	static isEnabled(node: ProseMirrorNode) {
		return node.isText && node.marks.some(m => m.type.name === PlaceholderMark.prototype.name);
	}

	// Determine if somewhere in a Selection has the placeholder mark enabled
	static selectionContains(selection: Selection) {
		let isContain = false;
		selection.content().content.descendants(node => {
			if (PlaceholderMark.isEnabled(node)) {
				isContain = true;
				return false;
			}
			return true;
		});
		return isContain;
	}

	private isPosInMiddleOfPlaceholder(pos: ResolvedPos) {
		const { nodeBefore, nodeAfter } = pos;
		if (!nodeBefore || !nodeAfter) return false;
		const isNodeBeforePlaceholder = PlaceholderMark.isEnabled(nodeBefore);
		const isNodeAfterPlaceholder = PlaceholderMark.isEnabled(nodeAfter);
		return isNodeBeforePlaceholder && isNodeAfterPlaceholder;
	}

	private isPosAtStartOfPlaceholder(pos: ResolvedPos) {
		const { nodeBefore, nodeAfter } = pos;
		if (!nodeAfter) return false;
		const isNodeAfterPlaceholder = PlaceholderMark.isEnabled(nodeAfter);
		const isNodeBeforePlaceholder = nodeBefore && PlaceholderMark.isEnabled(nodeBefore);
		return isNodeAfterPlaceholder && !isNodeBeforePlaceholder;
	}

	/**
	 * Find the START position of the first possible placeholder BEFORE a given position, backwardly.
	 * if the given position doesn't involved with a placeholder (the node right before
	 * it isn't a placeholder or it is not inside a placeholder), the give pos will be return.
	 * @param state EditorState
	 * @param pos the position where to start looking for a placeholder
	 * @returns the start position of a placeholder
	 */
	private findPlaceholderStartBefore(state: EditorState, pos: number): number {
		const { doc } = state;
		const resPos = doc.resolve(pos);
		const { nodeBefore } = resPos;
		if (nodeBefore && PlaceholderMark.isEnabled(nodeBefore)) {
			return this.findPlaceholderStartBefore(state, pos - nodeBefore.nodeSize);
		}
		return pos;
	}

	/**
	 * Find the END position of the first possible placeholder AFTER a given position, forwardly.
	 * if the given position doesn't involved with a placeholder (the node right after
	 * it isn't a placeholder or it is not inside a placeholder), the give pos will be return.
	 * @param state EditorState
	 * @param pos the position where to start looking for a placeholder
	 * @returns the end position of a placeholder
	 */
	private findPlaceholderEndAfter(state: EditorState, pos: number): number {
		const { doc } = state;
		const resPos = doc.resolve(pos);
		const { nodeAfter } = resPos;
		if (nodeAfter && PlaceholderMark.isEnabled(nodeAfter)) {
			return this.findPlaceholderEndAfter(state, pos + nodeAfter.nodeSize);
		}
		return pos;
	}

	/***
	 * Given a fragment, remove the placeholder marks inside it base on a predicate.
	 * @param predicate The predicate is a function that takes in a node and return a boolean
	 * to determine whether the placeholder mark on that node needs to be removed
	 * @returns return a function that takes in a fragment and return a fragment that is
	 * "de-placeholder" (meaning the 'placeholder' marks in this fragment are removed)
	 */
	private removeMark(predicate: (node: ProseMirrorNode) => boolean) {
		return function removeMarkByPredicate(fragment: Fragment): Fragment {
			const nodes: ProseMirrorNode[] = [];
			fragment.forEach(node => {
				if (PlaceholderMark.isEnabled(node) && predicate(node)) {
					const markType = node.type.schema.marks["placeholder_mark"];
					nodes.push(node.mark(markType.removeFromSet(node.marks)));
				} else {
					nodes.push(node.copy(removeMarkByPredicate(node.content)));
				}
			});
			return Fragment.fromArray(nodes);
		};
	}
}
